// Sessione del partecipante, per lobby e per scheda.
//
// E la ragione per cui un F5 non scollega piu: prima il nome viveva solo in
// location.state, quindi dopo un refresh il componente non apriva nemmeno il
// websocket e la pagina restava su "Loading..." per sempre.
//
// I depositi sono due, e servono entrambi:
//
// - `sessionStorage` dice a *questa scheda* chi e. E per scheda, quindi piu
//   partecipanti aperti nella stessa finestra non si sovrascrivono piu il
//   token a vicenda — prima l'ultimo entrato vinceva su tutti, e alla prima
//   ricarica due schede si presentavano al server con lo stesso token
//   sfrattandosi a vicenda (CLOSE_REPLACED) all'infinito;
//
// - `localStorage` tiene l'archivio dei token per nome, e sopravvive alla
//   chiusura del browser: e quello che permette di rientrare nella propria
//   squadra il giorno dopo digitando lo stesso nome.

const PREFIX = 'astafanta:session:';
// La scheda corrente. Chiave diversa dall'archivio perche i due depositi
// convivono e contengono cose diverse: una sessione sola contro tutte quelle
// che questo browser conosce.
const TAB_PREFIX = 'astafanta:scheda:';
// Prefisso separato: sullo stesso computer possono convivere la sessione da
// partecipante e quella dello schermo da proiettare, e non devono sovrascriversi.
const SPECTATOR_PREFIX = 'astafanta:spettatore:';

function storage(kind) {
    try {
        return window[kind];
    } catch {
        // Safari in navigazione privata, o storage disabilitato.
        return null;
    }
}

function read(kind, key) {
    const store = storage(kind);
    if (!store) return null;
    try {
        const raw = store.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function write(kind, key, value) {
    const store = storage(kind);
    if (!store) return;
    try {
        store.setItem(key, JSON.stringify(value));
    } catch {
        /* quota piena: la sessione resta valida solo per questa scheda */
    }
}

function drop(kind, key) {
    const store = storage(kind);
    if (!store) return;
    try {
        store.removeItem(key);
    } catch {
        /* niente da fare */
    }
}

/** Chiave d'archivio: lo stesso confronto che fa il server (`casefold`). */
function nameKey(name) {
    return String(name ?? '').trim().toLowerCase();
}

/** Tutte le sessioni che questo browser conosce per la lobby, per nome. */
function loadArchive(lobbyName) {
    const stored = read('localStorage', PREFIX + lobbyName);
    if (!stored) return {};
    // Formato vecchio: la sessione nuda, una sola per lobby. Si converte al
    // volo, cosi l'aggiornamento non butta fuori chi era gia collegato.
    if (stored.token) return { [nameKey(stored.name)]: stored };
    return stored;
}

function saveArchive(lobbyName, archive) {
    if (Object.keys(archive).length) write('localStorage', PREFIX + lobbyName, archive);
    else drop('localStorage', PREFIX + lobbyName);
}

export function saveSession(lobbyName, session) {
    write('sessionStorage', TAB_PREFIX + lobbyName, session);
    const archive = loadArchive(lobbyName);
    archive[nameKey(session.name)] = session;
    saveArchive(lobbyName, archive);
}

export function loadSession(lobbyName) {
    const mine = read('sessionStorage', TAB_PREFIX + lobbyName);
    if (mine?.token) return mine;

    // Nessuna scheda: browser riaperto, o indirizzo aperto in una scheda
    // nuova. Se in questo browser si e collegato un solo partecipante non c'e
    // ambiguita e la scheda diventa lui; se erano piu di uno bisogna dire
    // quale, e lo si fa dal portale digitando il nome.
    const known = Object.values(loadArchive(lobbyName)).filter((entry) => entry?.token);
    if (known.length !== 1) return null;
    write('sessionStorage', TAB_PREFIX + lobbyName, known[0]);
    return known[0];
}

/** Il token emesso a quel nome, per rientrare nella propria squadra. */
export function loadSessionByName(lobbyName, name) {
    const entry = loadArchive(lobbyName)[nameKey(name)];
    return entry?.token ? entry : null;
}

export function clearSession(lobbyName) {
    const mine = read('sessionStorage', TAB_PREFIX + lobbyName);
    drop('sessionStorage', TAB_PREFIX + lobbyName);
    if (!mine?.token) return;
    // Dall'archivio sparisce solo la voce di questa scheda: le altre squadre
    // aperte nello stesso browser non c'entrano nulla.
    const archive = loadArchive(lobbyName);
    delete archive[nameKey(mine.name)];
    saveArchive(lobbyName, archive);
}

// Sessione dello schermo da proiettare: solo un token, nessun nome e nessun
// partecipante dietro. Resta in localStorage e non per scheda — un proiettore
// e uno, e due schermi con lo stesso token il server li tiene su entrambi.

export function saveSpectatorSession(lobbyName, session) {
    write('localStorage', SPECTATOR_PREFIX + lobbyName, session);
}

export function loadSpectatorSession(lobbyName) {
    const stored = read('localStorage', SPECTATOR_PREFIX + lobbyName);
    return stored?.token ? stored : null;
}

export function clearSpectatorSession(lobbyName) {
    drop('localStorage', SPECTATOR_PREFIX + lobbyName);
}
